import { createContext, useEffect, useMemo } from 'react';
import { createContextualCan } from '@casl/react';
import { createMongoAbility } from '@casl/ability';
import { useGetUserPermissionsQuery } from '../features/Users/userApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  setAccessToken,
  selectCurrentPermissions,
  selectCurrentUserEmpCode,
  setPermissions,
} from '../features/Auth/authSlice';
import useSocket from '../hooks/sockets/useSocket';
import { useRefreshTokenQuery } from '../features/Auth/authApiSlice';

export const AbilityContext = createContext();
export const Can = createContextualCan(AbilityContext.Consumer);

export const AbilityProvider = ({ children }) => {
  const dispatch = useDispatch();
  const empCode = useSelector(selectCurrentUserEmpCode);
  const { data, refetch } = useGetUserPermissionsQuery(empCode, {
    skip: !empCode,
  });
  const { socket } = useSocket();
  const { data: refreshData, refetch: refetchToken } = useRefreshTokenQuery(undefined, { skip: !empCode });

  useEffect(() => {
    if (data && empCode) {
      dispatch(setPermissions(data?.data?.permissions));
    }
  }, [data, empCode, dispatch]);

  useEffect(() => {
    const handlePermissionsUpdate = async ({ empCode: updatedEmpCode }) => {
      if (updatedEmpCode === empCode) {
        await refetch();
        await refetchToken();
      }
    };

    socket.on('user-permissions-update', handlePermissionsUpdate);

    return () => {
      socket.off('user-permissions-update', handlePermissionsUpdate);
    };
  }, [socket, empCode, refetch, refetchToken]);

  useEffect(() => {
    if (refreshData) {
      dispatch(setAccessToken(refreshData));
    }
  }, [refreshData, dispatch]);

  const rules = useSelector(selectCurrentPermissions);
  const ability = useMemo(() => createMongoAbility(rules), [rules]);
  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
};
